import React, { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  DollarSign, 
  TrendingUp, 
  TrendingDown, 
  AlertTriangle, 
  Calendar,
  PieChart,
  BarChart3,
  Calculator,
  CreditCard
} from 'lucide-react';
import { useSoftwares } from '@/hooks/useSoftwares';
import { Software } from '@/types/software';

interface BillingMetrics {
  totalMensal: number;
  totalAnual: number;
  gastosPorCategoria: { [key: string]: number };
  softwaresMaisCustos: Software[];
  economiasPotenciais: number;
  licencasSubutilizadas: Software[];
  vencimentosProximos: Software[];
  custoMedioPorUsuario: number;
}

export function BillingTab() {
  const { data: softwares } = useSoftwares();
  const [periodoFiltro, setPeriodoFiltro] = React.useState('mensal');

  const billingMetrics = useMemo((): BillingMetrics => {
    if (!softwares) {
      return {
        totalMensal: 0,
        totalAnual: 0,
        gastosPorCategoria: {},
        softwaresMaisCustos: [],
        economiasPotenciais: 0,
        licencasSubutilizadas: [],
        vencimentosProximos: [],
        custoMedioPorUsuario: 0
      };
    }

    // Calcular custos totais
    const totalMensal = softwares.reduce((acc, software) => {
      const valorUnitario = software.valor_unitario || 0;
      const quantidade = software.quantidade_contratada || 0;
      
      if (software.tipo_licenciamento === 'assinatura') {
        return acc + (valorUnitario * quantidade);
      } else if (software.tipo_licenciamento === 'por_usuario') {
        return acc + (valorUnitario * quantidade);
      }
      return acc;
    }, 0);

    const totalAnual = totalMensal * 12;

    // Gastos por categoria/tipo
    const gastosPorCategoria = softwares.reduce((acc, software) => {
      const tipo = software.tipo_licenciamento;
      const valor = (software.valor_unitario || 0) * (software.quantidade_contratada || 0);
      acc[tipo] = (acc[tipo] || 0) + valor;
      return acc;
    }, {} as { [key: string]: number });

    // Softwares mais custosos
    const softwaresMaisCustos = [...softwares]
      .map(software => ({
        ...software,
        custoTotal: (software.valor_unitario || 0) * (software.quantidade_contratada || 0)
      }))
      .sort((a, b) => b.custoTotal - a.custoTotal)
      .slice(0, 5);

    // Licenças subutilizadas (menos de 70% de uso)
    const licencasSubutilizadas = softwares.filter(software => {
      const utilizacao = software.licencas_em_uso || 0;
      const contratadas = software.quantidade_contratada || 0;
      return contratadas > 0 && (utilizacao / contratadas) < 0.7;
    });

    // Economias potenciais (licenças não utilizadas)
    const economiasPotenciais = licencasSubutilizadas.reduce((acc, software) => {
      const naoUtilizadas = (software.quantidade_contratada || 0) - (software.licencas_em_uso || 0);
      const valorUnitario = software.valor_unitario || 0;
      return acc + (naoUtilizadas * valorUnitario);
    }, 0);

    // Vencimentos próximos (30 dias)
    const hoje = new Date();
    const em30Dias = new Date(hoje.getTime() + 30 * 24 * 60 * 60 * 1000);
    const vencimentosProximos = softwares.filter(software => {
      if (!software.data_vencimento) return false;
      const vencimento = new Date(software.data_vencimento);
      return vencimento >= hoje && vencimento <= em30Dias;
    });

    // Custo médio por usuário (estimativa)
    const totalUsuarios = softwares.reduce((acc, software) => {
      return acc + (software.licencas_em_uso || 0);
    }, 0);
    const custoMedioPorUsuario = totalUsuarios > 0 ? totalMensal / totalUsuarios : 0;

    return {
      totalMensal,
      totalAnual,
      gastosPorCategoria,
      softwaresMaisCustos,
      economiasPotenciais,
      licencasSubutilizadas,
      vencimentosProximos,
      custoMedioPorUsuario
    };
  }, [softwares]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  const tipoLicenciamentoLabels: Record<string, string> = {
    perpetua: 'Perpétua',
    assinatura: 'Assinatura',
    por_usuario: 'Por Usuário',
    por_dispositivo: 'Por Dispositivo',
    volume: 'Volume',
  };

  return (
    <div className="space-y-6">
      {/* Filtro de Período */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Relatórios Financeiros</h2>
        <Select value={periodoFiltro} onValueChange={setPeriodoFiltro}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Período" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="mensal">Mensal</SelectItem>
            <SelectItem value="anual">Anual</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Cards de Métricas Financeiras */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Gasto {periodoFiltro === 'mensal' ? 'Mensal' : 'Anual'}
            </CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatCurrency(periodoFiltro === 'mensal' ? billingMetrics.totalMensal : billingMetrics.totalAnual)}
            </div>
            <p className="text-xs text-muted-foreground">
              {periodoFiltro === 'mensal' ? 'Por mês' : 'Por ano'}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Economia Potencial</CardTitle>
            <TrendingDown className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {formatCurrency(billingMetrics.economiasPotenciais)}
            </div>
            <p className="text-xs text-muted-foreground">
              Licenças subutilizadas
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Custo por Usuário</CardTitle>
            <Calculator className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatCurrency(billingMetrics.custoMedioPorUsuario)}
            </div>
            <p className="text-xs text-muted-foreground">
              Média mensal
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Renovações Próximas</CardTitle>
            <AlertTriangle className="h-4 w-4 text-orange-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">
              {billingMetrics.vencimentosProximos.length}
            </div>
            <p className="text-xs text-muted-foreground">
              Próximos 30 dias
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Gráficos e Análises */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Gastos por Categoria */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <PieChart className="h-5 w-5" />
              Gastos por Tipo de Licença
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {Object.entries(billingMetrics.gastosPorCategoria).map(([tipo, valor]) => {
                const porcentagem = billingMetrics.totalMensal > 0 
                  ? (valor / billingMetrics.totalMensal) * 100 
                  : 0;
                
                return (
                  <div key={tipo} className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium">
                        {tipoLicenciamentoLabels[tipo] || tipo}
                      </span>
                      <span className="text-sm text-muted-foreground">
                        {formatCurrency(valor)} ({porcentagem.toFixed(1)}%)
                      </span>
                    </div>
                    <Progress value={porcentagem} className="h-2" />
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Softwares Mais Custosos */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              Top 5 Softwares Mais Custosos
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {billingMetrics.softwaresMaisCustos.map((software, index) => (
                <div key={software.id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Badge variant="outline" className="w-6 h-6 p-0 flex items-center justify-center">
                      {index + 1}
                    </Badge>
                    <div>
                      <p className="font-medium">{software.nome}</p>
                      <p className="text-xs text-muted-foreground">
                        {software.quantidade_contratada} licenças
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-bold">
                      {formatCurrency((software as any).custoTotal)}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {formatCurrency(software.valor_unitario || 0)}/licença
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabelas de Análise */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Licenças Subutilizadas */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingDown className="h-5 w-5 text-orange-600" />
              Licenças Subutilizadas
            </CardTitle>
          </CardHeader>
          <CardContent>
            {billingMetrics.licencasSubutilizadas.length === 0 ? (
              <p className="text-center text-muted-foreground py-4">
                Todas as licenças estão bem utilizadas! 🎉
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Software</TableHead>
                    <TableHead className="text-center">Uso</TableHead>
                    <TableHead className="text-right">Economia</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {billingMetrics.licencasSubutilizadas.slice(0, 5).map((software) => {
                    const utilizacao = ((software.licencas_em_uso || 0) / (software.quantidade_contratada || 1)) * 100;
                    const naoUtilizadas = (software.quantidade_contratada || 0) - (software.licencas_em_uso || 0);
                    const economia = naoUtilizadas * (software.valor_unitario || 0);
                    
                    return (
                      <TableRow key={software.id}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{software.nome}</p>
                            <p className="text-xs text-muted-foreground">
                              {software.licencas_em_uso}/{software.quantidade_contratada}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge variant={utilizacao < 50 ? 'destructive' : 'secondary'}>
                            {utilizacao.toFixed(0)}%
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right font-medium text-green-600">
                          {formatCurrency(economia)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Renovações Próximas */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-orange-600" />
              Renovações Próximas
            </CardTitle>
          </CardHeader>
          <CardContent>
            {billingMetrics.vencimentosProximos.length === 0 ? (
              <p className="text-center text-muted-foreground py-4">
                Nenhuma renovação nos próximos 30 dias
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Software</TableHead>
                    <TableHead className="text-center">Vencimento</TableHead>
                    <TableHead className="text-right">Valor</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {billingMetrics.vencimentosProximos.map((software) => {
                    const diasRestantes = software.data_vencimento 
                      ? Math.ceil((new Date(software.data_vencimento).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))
                      : 0;
                    const valorTotal = (software.valor_unitario || 0) * (software.quantidade_contratada || 0);
                    
                    return (
                      <TableRow key={software.id}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{software.nome}</p>
                            <p className="text-xs text-muted-foreground">
                              {software.quantidade_contratada} licenças
                            </p>
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge variant={diasRestantes <= 7 ? 'destructive' : 'secondary'}>
                            {diasRestantes} dias
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          {formatCurrency(valorTotal)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Resumo e Recomendações */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            Resumo e Recomendações
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h4 className="font-semibold text-green-600">💡 Oportunidades de Economia</h4>
              <ul className="space-y-2 text-sm">
                {billingMetrics.licencasSubutilizadas.length > 0 && (
                  <li className="flex items-start gap-2">
                    <span className="text-green-600">•</span>
                    Reduzir {billingMetrics.licencasSubutilizadas.length} licenças subutilizadas pode economizar {formatCurrency(billingMetrics.economiasPotenciais)}/mês
                  </li>
                )}
                {billingMetrics.vencimentosProximos.length > 0 && (
                  <li className="flex items-start gap-2">
                    <span className="text-orange-600">•</span>
                    Revisar {billingMetrics.vencimentosProximos.length} contratos próximos ao vencimento
                  </li>
                )}
                <li className="flex items-start gap-2">
                  <span className="text-blue-600">•</span>
                  Considerar negociação de volume para softwares mais custosos
                </li>
              </ul>
            </div>
            
            <div className="space-y-4">
              <h4 className="font-semibold text-blue-600">📊 Insights Financeiros</h4>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-blue-600">•</span>
                  Gasto médio por usuário: {formatCurrency(billingMetrics.custoMedioPorUsuario)}/mês
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-600">•</span>
                  Projeção anual: {formatCurrency(billingMetrics.totalAnual)}
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-600">•</span>
                  {Object.keys(billingMetrics.gastosPorCategoria).length} tipos de licenciamento em uso
                </li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
